import MonacoEditor from "@via/components/monaco-editor/monaco-editor";
import SelectComponent from "@via/components/select/select";
import { ScriptFilesList } from "@via/pages/projects/pipelines/workflow/components/script-files-list.tsx";
import {
  EDIT_PROCESS_ADVANCED_FILES,
  LANGUAGE_MODES,
  LANGUAGE_OPTIONS,
} from "@via/utils/constants.tsx";
import { AnyRef } from "@via/utils/interfaces";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useRegisterProcessScriptsCortexContexts } from "@via/hooks/useRegisterProcessScriptsCortexContexts";
import {
  clearCortexSelection,
  getCortexSelectionForCurrentPage,
} from "@via/utils/cortex-selection";
import { attachCortexSelectionTracking } from "@via/utils/monaco-cortex-selection";
import {
  clearCortexMonacoTarget,
  setCortexMonacoTarget,
} from "@via/utils/cortex-monaco-target";

export const SCRIPT = "body";

export default function Scripts({
  draft,
  language,
  handleEditorChange,
  handleLanguageChange,
  selectedFile,
  setSelectedFile,
  canEditProcess,
}) {
  const editorRef: AnyRef = useRef(null);
  const selectionMetaRef = useRef({
    language,
    filePath: selectedFile ?? "",
  });
  const selectionDisposableRef = useRef<{ dispose?: () => void } | null>(null);
  const lastSelectedTextRef = useRef<string>("");
  const lastSelectedFileRef = useRef<string | null>(null);

  const scriptContent =
    (draft?.script?.[selectedFile] as string | undefined) ?? "";

  useEffect(() => {
    selectionMetaRef.current = {
      language,
      filePath: selectedFile ?? "",
    };
  }, [language, selectedFile]);

  useEffect(() => {
    const nextSelectedFile = selectedFile ?? null;
    const prevSelectedFile = lastSelectedFileRef.current;
    lastSelectedFileRef.current = nextSelectedFile;
    if (!prevSelectedFile) return;
    if (nextSelectedFile === prevSelectedFile) return;

    // Reset selection when switching script files (Run Logs-like behavior)
    clearCortexSelection();
    lastSelectedTextRef.current = "";
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      selectionDisposableRef.current?.dispose?.();
      selectionDisposableRef.current = null;
      clearCortexSelection();
      lastSelectedTextRef.current = "";
      clearCortexMonacoTarget();
    };
  }, []);

  const getProcessScriptsContext = useCallback(() => {
    const selection = getCortexSelectionForCurrentPage();
    const selectedCode =
      selection && selection.filePath === selectedFile ? selection.text : "";
    const selectionLabel =
      selection && selectedCode
        ? `Selected code (${selection.filePath}):\n\`\`\`${selection.language || language}\n${selectedCode}\n\`\`\`\n`
        : "";
    return {
      processId: draft?.processId ?? null,
      processName: draft?.name ?? "",
      selectedFile,
      language,
      selectedCode,
      selectedCodeMeta:
        selection && selectedCode
          ? {
              language: selection.language,
              filePath: selection.filePath,
              updatedAt: selection.updatedAt,
            }
          : null,
      content: selectedCode ? selectionLabel : scriptContent,
    };
  }, [draft?.name, draft?.processId, language, scriptContent, selectedFile]);

  useRegisterProcessScriptsCortexContexts({
    getContext: getProcessScriptsContext,
    enabled: !!draft && !!selectedFile,
  });

  const defaultLanguage = useMemo(() => {
    const filterLanguage =
      draft?.script?.language === "sh"
        ? LANGUAGE_MODES.SHELL
        : draft?.script?.language;
    return LANGUAGE_OPTIONS.find(option => option.value === filterLanguage);
  }, [draft]);

  const handleMonacoMount = useCallback(editor => {
    setCortexMonacoTarget(editor);
    attachCortexSelectionTracking({
      editor,
      selectionMetaRef,
      selectionDisposableRef,
      lastSelectedTextRef,
    });
  }, []);

  return (
    <div className="flex flex-row overflow-hidden">
      <PanelGroup direction="horizontal">
        <Panel defaultSize={20} minSize={20} maxSize={30}>
          <div className="flex h-full flex-col justify-between">
            <ScriptFilesList
              files={EDIT_PROCESS_ADVANCED_FILES}
              selected={selectedFile}
              setSelected={setSelectedFile}
            />
            {selectedFile === SCRIPT && (
              <div className="mb-6 mr-4">
                <SelectComponent
                  name="scriptMode"
                  menuPlacement="top"
                  enableClear={false}
                  readOnly={!canEditProcess}
                  onChange={handleLanguageChange}
                  defaultValue={defaultLanguage}
                  options={LANGUAGE_OPTIONS}
                />
              </div>
            )}
          </div>
        </Panel>
        <PanelResizeHandle className="w-2 cursor-col-resize border-r-[1px] border-gray-200 duration-150 ease-in-out hover:border-r-2 hover:border-gray-300" />
        <Panel minSize={30}>
          <MonacoEditor
            value={draft.script?.[selectedFile]}
            onChange={canEditProcess ? handleEditorChange : null}
            onMount={handleMonacoMount}
            options={{
              readOnly: !canEditProcess,
              minimap: {
                enabled: false,
              },
            }}
            language={language}
            saveViewState={false}
            keepCurrentModel={false}
            editorRef={editorRef}
          />
        </Panel>
      </PanelGroup>
    </div>
  );
}
